import React, { ReactNode } from "react";
import {
  Select,
  TextArea,
  InputField,
  PhoneInput,
  MyButton,
  AddSection,
} from "../../components/Component";
import { FaHandSparkles } from "react-icons/fa";
import {
  FiPhone,
  FiShare2,
  FiMapPin,
  FiSettings,
  FiAlignLeft,
} from "react-icons/fi";

/**
 * Shared section blocks for DoSee Data "Add" pages — keeps the visual
 * grouping consistent across 30+ entity types while letting each page
 * keep its own state names, payload shape and unique fields.
 */

type Setter = (v: any) => any;

/* ─────────── Contact ─────────── */
export const ContactSection = ({
  phone,
  setPhone,
  email,
  setEmail,
  payLink,
  setPayLink,
  clearMessages,
}: {
  phone: any;
  setPhone: Setter;
  email: any;
  setEmail: Setter;
  payLink?: any;
  setPayLink?: Setter;
  clearMessages?: () => void;
}) => (
  <AddSection
    title="Contact"
    hint="How travellers can reach this listing."
    icon={<FiPhone className="h-4 w-4" />}
  >
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <PhoneInput setPhone={setPhone} phone={phone} required={false} />
      <InputField
        props={{
          id: "email",
          title: "Email Address",
          type: "email",
          value: email || "",
          onChange: (e: any) => {
            setEmail(e.target.value);
            clearMessages?.();
          },
        }}
      />
      {setPayLink ? (
        <InputField
          props={{
            id: "pay-link",
            title: "Pay Link (Optional)",
            type: "text",
            value: payLink || "",
            onChange: (e: any) => {
              setPayLink(e.target.value);
              clearMessages?.();
            },
          }}
        />
      ) : null}
    </div>
  </AddSection>
);

/* ─────────── Social ─────────── */
export const SocialSection = ({
  whatsapp,
  setWhatsapp,
  facebook,
  setFacebook,
  twitter,
  setTwitter,
  instagram,
  setInstagram,
  youtube,
  setYoutube,
  clearMessages,
}: {
  whatsapp: any;
  setWhatsapp: Setter;
  facebook: any;
  setFacebook: Setter;
  twitter: any;
  setTwitter: Setter;
  instagram: any;
  setInstagram: Setter;
  youtube: any;
  setYoutube: Setter;
  clearMessages?: () => void;
}) => {
  const fields: Array<[string, string, any, Setter]> = [
    ["whatsapp", "Whatsapp", whatsapp, setWhatsapp],
    ["facebook", "Facebook", facebook, setFacebook],
    ["twitter", "Twitter", twitter, setTwitter],
    ["instagram", "Instagram", instagram, setInstagram],
    ["youtube", "YouTube", youtube, setYoutube],
  ];
  return (
    <AddSection
      title="Social"
      hint="Optional public handles."
      icon={<FiShare2 className="h-4 w-4" />}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {fields.map(([id, label, value, setter]) => (
          <InputField
            key={id}
            props={{
              id,
              title: label,
              type: "text",
              value: value || "",
              onChange: (e: any) => {
                setter(e.target.value);
                clearMessages?.();
              },
            }}
          />
        ))}
      </div>
    </AddSection>
  );
};

/* ─────────── Geo ─────────── */
export const GeoSection = ({
  googleMapCode,
  setGoogleMapCode,
  latitude,
  setLatitude,
  longitude,
  setLongitude,
  clearMessages,
}: {
  googleMapCode: any;
  setGoogleMapCode: Setter;
  latitude: any;
  setLatitude: Setter;
  longitude: any;
  setLongitude: Setter;
  clearMessages?: () => void;
}) => (
  <AddSection
    title="Geo Position"
    hint="Coordinates and Google Maps embed code."
    icon={<FiMapPin className="h-4 w-4" />}
  >
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <InputField
        props={{
          id: "google_map_code",
          title: "Google Map Code",
          type: "text",
          value: googleMapCode || "",
          onChange: (e: any) => {
            setGoogleMapCode(e.target.value);
            clearMessages?.();
          },
        }}
      />
      <InputField
        props={{
          id: "latitude",
          title: "Latitude",
          type: "text",
          value: latitude || "",
          onChange: (e: any) => {
            setLatitude(e.target.value);
            clearMessages?.();
          },
        }}
      />
      <InputField
        props={{
          id: "longitude",
          title: "Longitude",
          type: "text",
          value: longitude || "",
          onChange: (e: any) => {
            setLongitude(e.target.value);
            clearMessages?.();
          },
        }}
      />
    </div>
  </AddSection>
);

/* ─────────── Visibility ─────────── */
type VisibilitySectionProps = {
  statusId: string;
  setStatus: Setter;
  setFeatured: Setter;
  setPromoted: Setter;
  setExpo: Setter;
  /** Optional current values — provide these on Edit pages so each Select pre-selects the current value */
  status?: any;
  featured?: any;
  promoted?: any;
  expo?: any;
  clearMessages?: () => void;
};

const yn = (v: any) => (v === 1 || v === "1" || v === "yes" ? "yes" : "no");
const visIvis = (v: any) =>
  v === 1 || v === "1" || v === "visible" ? "visible" : "invisible";

export const VisibilitySection = ({
  statusId,
  setStatus,
  setFeatured,
  setPromoted,
  setExpo,
  status,
  featured,
  promoted,
  expo,
  clearMessages,
}: VisibilitySectionProps) => (
  <AddSection
    title="Visibility"
    hint="Where this entry appears across the site."
    icon={<FiSettings className="h-4 w-4" />}
  >
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <Select
        props={{
          id: statusId,
          title: "Status",
          option: ["visible", "invisible"],
          preselect:
            status !== undefined && status !== null
              ? [{ name: visIvis(status) }]
              : null,
          onChange: (e: any) => {
            if (e.target.selectedIndex === 0) setStatus(1);
            else setStatus(0);
            clearMessages?.();
          },
        }}
      />
      <Select
        props={{
          id: "featured",
          title: "Featured",
          option: ["no", "yes"],
          preselect:
            featured !== undefined && featured !== null
              ? [{ name: yn(featured) }]
              : null,
          onChange: (e: any) => {
            setFeatured(e.target.value);
            clearMessages?.();
          },
        }}
      />
      <Select
        props={{
          id: "promoted",
          title: "Promoted",
          option: ["no", "yes"],
          preselect:
            promoted !== undefined && promoted !== null
              ? [{ name: yn(promoted) }]
              : null,
          onChange: (e: any) => {
            setPromoted(e.target.value);
            clearMessages?.();
          },
        }}
      />
      <Select
        props={{
          id: "gallery",
          title: "Show in Gallery",
          option: ["no", "yes"],
          preselect:
            expo !== undefined && expo !== null
              ? [{ name: yn(expo) }]
              : null,
          onChange: (e: any) => {
            const choice = e.target.value;
            setExpo(choice === "no" ? 0 : 1);
            clearMessages?.();
          },
        }}
      />
    </div>
  </AddSection>
);

/* ─────────── Description with Generate button ─────────── */
export const DescriptionSection = ({
  description,
  setDescription,
  hashtags,
  setHashtags,
  onGenerate,
  hint,
  clearMessages,
  extra,
}: {
  description: any;
  setDescription: Setter;
  hashtags?: any;
  setHashtags?: Setter;
  onGenerate?: () => void;
  hint?: string;
  clearMessages?: () => void;
  extra?: ReactNode;
}) => (
  <AddSection
    title="Description"
    hint={hint || "What travellers should know about this entry."}
    icon={<FiAlignLeft className="h-4 w-4" />}
    actions={
      onGenerate ? (
        <MyButton
          onClick={onGenerate}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-brand to-primary px-3 py-2 text-xs font-semibold text-white shadow hover:opacity-95"
        >
          <FaHandSparkles className="h-4 w-4" />
          Generate
        </MyButton>
      ) : undefined
    }
  >
    <TextArea
      props={{
        id: "description",
        title: "Description",
        type: "text",
        value: description || "",
        onChange: (e: any) => {
          setDescription(e.target.value);
          clearMessages?.();
        },
      }}
    />
    {setHashtags ? (
      <div className="mt-4">
        <InputField
          props={{
            id: "hashtag",
            title: "Tags (comma separated)",
            type: "text",
            value: hashtags || "",
            onChange: (e: any) => {
              setHashtags(e.target.value);
              clearMessages?.();
            },
          }}
        />
      </div>
    ) : null}
    {extra}
  </AddSection>
);
